import { isRuleGroup } from "./queryBuilderConfig.js";

export function ruleGroupHasRules(query) {
  if (!query?.rules?.length) return false;
  return query.rules.some((r) => (isRuleGroup(r) ? ruleGroupHasRules(r) : Boolean(r.field)));
}

function quoteValue(value) {
  if (value == null || value === "") return "''";
  const trimmed = String(value).trim();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
  return `'${trimmed.replace(/'/g, "''")}'`;
}

function formatRule(rule) {
  if (!rule.field) return "";
  const { field, operator, value } = rule;
  switch (operator) {
    case "null":
      return `${field} IS NULL`;
    case "notNull":
      return `${field} IS NOT NULL`;
    case "contains":
      return `${field} LIKE ${quoteValue(`%${value}%`)}`;
    case "doesNotContain":
      return `${field} NOT LIKE ${quoteValue(`%${value}%`)}`;
    case "beginsWith":
      return `${field} LIKE ${quoteValue(`${value}%`)}`;
    case "endsWith":
      return `${field} LIKE ${quoteValue(`%${value}`)}`;
    case "in": {
      const parts = String(value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map(quoteValue);
      return parts.length ? `${field} IN (${parts.join(", ")})` : "";
    }
    case "notIn": {
      const parts = String(value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map(quoteValue);
      return parts.length ? `${field} NOT IN (${parts.join(", ")})` : "";
    }
    case "between": {
      const [a, b] = String(value).split(",").map((v) => v.trim());
      if (!a || !b) return "";
      return `${field} BETWEEN ${quoteValue(a)} AND ${quoteValue(b)}`;
    }
    case "notBetween": {
      const [a, b] = String(value).split(",").map((v) => v.trim());
      if (!a || !b) return "";
      return `${field} NOT BETWEEN ${quoteValue(a)} AND ${quoteValue(b)}`;
    }
    case "expr":
    case "case":
      return value ? `${value} AS ${field}` : "";
    default:
      if (value === "" && operator !== "null" && operator !== "notNull") return "";
      return `${field} ${operator} ${quoteValue(value)}`;
  }
}

function formatGroup(query) {
  if (!query?.rules?.length) return "";
  const joiner = ` ${(query.combinator ?? "and").toUpperCase()} `;
  const parts = query.rules
    .map((item) => {
      const sql = isRuleGroup(item) ? formatGroup(item) : formatRule(item);
      if (!sql) return "";
      const wrapped = isRuleGroup(item) ? `(${sql})` : sql;
      return item.not ? `NOT (${wrapped})` : wrapped;
    })
    .filter(Boolean);
  if (!parts.length) return "";
  const body = parts.join(joiner);
  return query.not ? `NOT (${body})` : body;
}

export function formatRuleGroupSql(query) {
  if (!query || !ruleGroupHasRules(query)) return "";
  return formatGroup(query);
}

export function formatRuleGroupLabel(query, fallback = "No conditions") {
  const sql = formatRuleGroupSql(query);
  return sql || fallback;
}

function extractThenFromQuery(query) {
  if (!query?.rules) return "";
  for (const item of query.rules) {
    if (isRuleGroup(item)) {
      const nested = extractThenFromQuery(item);
      if (nested) return nested;
    } else if ((item.operator === "expr" || item.operator === "case") && item.value) {
      return String(item.value).trim();
    }
  }
  return "";
}

function formatThenClause(then, query) {
  const expr = then?.trim() || extractThenFromQuery(query);
  if (!expr) return "NULL";
  if (/^-?\d+(\.\d+)?$/.test(expr)) return expr;
  if (/^[a-zA-Z_][\w]*(\s*[+\-*/]\s*[\w.]+)*$/.test(expr)) return expr;
  return quoteValue(expr);
}

export function formatCalculatedFieldSql(field) {
  const name = field.name?.trim();
  if (!name) return "";

  const logics = (field.logics ?? []).filter((l) => ruleGroupHasRules(l.query));
  if (!logics.length) return "";

  if (logics.length === 1 && !field.elseExpression?.trim()) {
    const only = logics[0];
    const cond = formatRuleGroupSql(only.query);
    const then = formatThenClause(only.then, only.query);
    if (only.then?.trim() || extractThenFromQuery(only.query)) {
      return `CASE WHEN (${cond}) THEN ${then} END AS ${name}`;
    }
    return `(${cond}) AS ${name}`;
  }

  const branches = logics
    .map((l) => {
      const when = formatRuleGroupSql(l.query);
      if (!when) return "";
      return `WHEN (${when}) THEN ${formatThenClause(l.then, l.query)}`;
    })
    .filter(Boolean);

  if (!branches.length) return "";
  const elseClause = field.elseExpression?.trim() ? ` ELSE ${formatThenClause(field.elseExpression, null)}` : "";
  return `CASE ${branches.join(" ")}${elseClause} END AS ${name}`;
}
